import { useEffect } from "react";
import ReactGA from "react-ga4";

import {
  Box,
  Link,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import PageLayout, { PageHeader } from "../components/layout/PageLayout";
import { TONE, TYPE } from "../styles/theme";

const SECTIONS = [
  {
    id: "overview",
    heading: "The short version",
    body: (
      <>
        Kuthé helps you find independent, owner-operated coffee shops nearby.{" "}
        You can browse the site and the app without telling us who you are. We collect
        as little as possible - only what's needed to make the product work.
      </>
    ),
  },
  {
    id: "location",
    heading: "Location",
    body: (
      <>
        When you tap <strong>Find Near Me</strong> (web) or open the app, we ask for your
        device location to show nearby coffee shops. Your coordinates are sent to our
        server to run that search and <strong>are not kept</strong>.
        On the website, your browser may keep the last search coordinates in{" "}
        <strong>session storage</strong> for this tab only so refreshing Explore does not
        lose your place; that data stays on your device and clears when you close the tab.
        On mobile, the Google Maps SDK also receives your
        location to render the map; this is governed by Google's{" "}
        <Link href="https://policies.google.com/privacy" isExternal color="inherit" textDecoration="underline">
          privacy policy
        </Link>
        . You can always deny location permission; the app falls back to a manual search.
      </>
    ),
  },
  {
    id: "analytics",
    heading: "Analytics",
    body: (
      <>
        The website uses <strong>Google Analytics 4</strong>; the mobile app uses{" "}
        <strong>PostHog</strong>. Both collect anonymous usage data (for example searches
        and shop interactions) so we can improve the product. The app ties events to a
        random ID on your device - not your name, email, or phone. Both also use the IP
        address of your internet connection to approximate your location - city level,
        not your device's GPS - so we can see where Kuthé is used. See Google's and{" "}
        <Link href="https://posthog.com/privacy" isExternal color="inherit" textDecoration="underline">
          PostHog's
        </Link>{" "}
        privacy policies for how they process that data.
      </>
    ),
  },
  {
    id: "search",
    heading: "Location search",
    body: (
      <>
        The search bar uses <strong>Mapbox</strong> to turn place names into coordinates.
        Your query and an approximate location (inferred from your IP by Mapbox for
        relevance) are sent to Mapbox's servers. We don't keep your searches on our own
        servers; in the mobile app, what you type is included in the anonymous analytics
        described above. Mapbox's{" "}
        <Link href="https://www.mapbox.com/legal/privacy" isExternal color="inherit" textDecoration="underline">
          privacy policy
        </Link>{" "}
        applies to those requests.
      </>
    ),
  },
  {
    id: "forms",
    heading: "Contact & feedback forms",
    body: (
      <>
        If you submit a message via the <strong>Contact</strong> form, we collect your
        name, email address, and message. The{" "}
        <strong>Feedback</strong> form (available on both the website and in the mobile
        app's Settings screen) collects only a free-text message. Previous homepage waitlist
        signups collected an email address only. These forms are handled by{" "}
        <strong>Netlify Forms</strong> and stored on Netlify's infrastructure. We use
        a honeypot field (and, when enabled, a CAPTCHA) to reduce spam - these are not
        used to identify you. We use form data only to respond to you. Shop owner
        submissions work differently and are covered below.
      </>
    ),
  },
  {
    id: "mobile",
    heading: "Mobile app",
    body: (
      <>
        The app stores these on your device using AsyncStorage (a local key-value store):
        <VStack align="start" spacing={1} mt={3} pl={2}>
          <Text as="span">- <strong>Saved shops</strong> - shops you've bookmarked</Text>
          <Text as="span">- <strong>Recent searches</strong> - your last few location queries</Text>
          <Text as="span">- <strong>Onboarding state</strong> - whether you've seen the intro slides</Text>
          <Text as="span">- <strong>Theme preference</strong> - your light or dark mode choice</Text>
          <Text as="span">- <strong>Analytics ID</strong> - a random ID for anonymous product analytics</Text>
        </VStack>
        <Box mt={3}>
          None of this identifies you by name. Saved shops, searches, onboarding, and theme
          stay on your device. The analytics ID is used only to send anonymous usage events
          to PostHog. All of it is deleted if you uninstall the app or clear its storage.
        </Box>
        <Box mt={3}>
          If you turn on <strong>notifications</strong>, we store a <strong>push token</strong> - an
          address Apple or Google gives your device so we can reach it - and which kinds of
          notification you want. Nothing else: no name, no account, no location. Turn
          notifications off in Settings and we stop sending; delete the app and the token stops
          working, and we remove it the next time we send.
        </Box>
      </>
    ),
  },
  {
    id: "reports",
    heading: "Shop reports",
    body: (
      <>
        If you flag a shop as a chain or incorrectly listed, we store the{" "}
        <strong>reason you selected</strong> and a <strong>one-way hash</strong> (SHA-256)
        of your IP address. The hash is used only to prevent the same person flagging the
        same shop repeatedly. It cannot be reversed to identify you. We never store your
        raw IP address.
      </>
    ),
  },
  {
    id: "owner-submissions",
    heading: "Shop owner submissions",
    body: (
      <>
        If you own or work at a shop and tell us about it, what you say about the shop
        goes on its page. That's the point of asking. Your own <strong>name and email</strong>,
        if you give them, are kept separately, are <strong>never shown on the site or in
        the app</strong>, and are only so we can check with you before changing anything.
        Unlike the forms above, this is stored in our own database rather than on Netlify.
        Nothing is published until a person has read it. Reply to any email from us, or
        use the contact form, and we'll delete the lot.
      </>
    ),
  },
  {
    id: "third-parties",
    heading: "Third-party services",
    body: (
      <>
        We use these external services. Each has its own privacy policy:
        <VStack align="start" spacing={2} mt={3} pl={2}>
          <Text as="span"><strong>Google Maps</strong> - displays the map and provides place data; the Google Maps SDK on mobile may collect location and usage data per Google's privacy policy</Text>
          <Text as="span"><strong>Google Analytics</strong> - anonymous usage stats on the web</Text>
          <Text as="span"><strong>PostHog</strong> - anonymous product analytics in the mobile app</Text>
          <Text as="span"><strong>Mapbox</strong> - turns what you type in the search bar into coordinates</Text>
          <Text as="span">
            <strong>OpenStreetMap</strong> - used to place shops on the map. Location data
            © OpenStreetMap contributors, under the{" "}
            <Link href="https://www.openstreetmap.org/copyright" isExternal color="inherit" textDecoration="underline">
              Open Database License
            </Link>
            .
          </Text>
          <Text as="span"><strong>Netlify</strong> - hosts the website and handles contact forms</Text>
          <Text as="span"><strong>Expo</strong> - delivers app notifications to Apple and Google, if you turn them on</Text>
          <Text as="span">
            <strong>Anthropic (Claude)</strong> - generates the written descriptions and tags
            for coffee shops. We send shop names, websites, and publicly available information
            about shops. If a shop owner photographs a coffee bag for us, we send that image
            too, so the label can be read. <em>We never send data about people browsing Kuthé
            to Anthropic.</em>
          </Text>
          <Text as="span">
            <strong>OpenAI</strong> - transcribes voice notes recorded by shop owners on the
            owner submission form. Nothing else is sent to OpenAI.
          </Text>
        </VStack>
      </>
    ),
  },
  {
    id: "rights",
    heading: "Your rights",
    body: (
      <>
        <strong>GDPR (EU/UK residents):</strong> You have the right to access, correct,
        or delete any personal data we hold about you. Since we store almost nothing tied
        to individuals, this mainly applies to contact form submissions.{" "}
        <Link href="/contact" color="inherit" textDecoration="underline">
          Contact us
        </Link>{" "}
        and we'll handle it promptly.
        <Box mt={3}>
          <strong>CCPA (California residents):</strong> We do not sell personal
          information. We do not share it for cross-context behavioural advertising.
        </Box>
      </>
    ),
  },
  {
    id: "cookies",
    heading: "Cookies",
    body: (
      <>
        The website uses cookies set by <strong>Google Analytics 4</strong> to track
        anonymous usage. No other cookies are set by us. There is no consent banner yet -
        we're aware this may be required depending on your jurisdiction and are working on
        it. If you want to opt out of GA tracking now, the{" "}
        <Link href="https://tools.google.com/dlpage/gaoptout" isExternal color="inherit" textDecoration="underline">
          Google Analytics opt-out browser add-on
        </Link>{" "}
        works. The mobile app sets no cookies.
      </>
    ),
  },
  {
    id: "children",
    heading: "Children",
    body: (
      <>
        The app is rated 4+ and is appropriate for all ages. We don't collect anything
        that identifies a user personally.
      </>
    ),
  },
  {
    id: "changes",
    heading: "Changes to this policy",
    body: (
      <>
        If we make material changes, we'll update the date at the bottom of this page. We
        won't quietly change how we handle your data - anything significant will be
        clearly noted.
      </>
    ),
  },
  {
    id: "contact",
    heading: "Questions?",
    body: (
      <>
        Use the{" "}
        <Link href="/contact" color="inherit" textDecoration="underline">
          contact form
        </Link>
        . We're a small team and we'll get back to you.
      </>
    ),
  },
];

export default function PrivacyPage() {
  const t      = useColorModeValue(TONE.light, TONE.dark);
  const dim    = t.dim;
  const accent = t.accent;
  const text   = t.text;
  const rule   = t.border;

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname, title: "privacy-policy", location: window.location.href });
  }, []);

  return (
    <PageLayout narrow>
      <PageHeader
        title="Privacy Policy"
        subtitle="We believe you deserve to know exactly what happens with your data. Here's the honest version - no legalese."
      />

      <VStack align="start" spacing={0}>
        {SECTIONS.map((section) => (
          <Box key={section.id} w="full">
            <Box py={8} borderTop="1px solid" borderColor={rule}>
              <Box pl={4} borderLeft="2px solid" borderColor={accent} mb={4}>
                <Text
                  as="h2"
                  fontFamily="heading"
                  fontSize={TYPE.blockTitle}
                  fontWeight={400}
                  color={text}
                  lineHeight={1.3}
                >
                  {section.heading}
                </Text>
              </Box>
              <Text fontSize={TYPE.body} lineHeight={1.8} color={dim}>
                {section.body}
              </Text>
            </Box>
          </Box>
        ))}

        <Box w="full" pt={8} borderTop="1px solid" borderColor={rule}>
          <Text fontSize={TYPE.meta} color={dim}>
            Last updated - October 2026
          </Text>
        </Box>
      </VStack>
    </PageLayout>
  );
}
